import type { ComposerDocument, DocumentSummary } from '../types';
import { newId } from './tree';

const DOCS_KEY = 'composer:documents';
const ACTIVE_KEY = 'composer:active-id';

function readDocs(): ComposerDocument[] {
  try {
    const raw = localStorage.getItem(DOCS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeDocs(docs: ComposerDocument[]) {
  localStorage.setItem(DOCS_KEY, JSON.stringify(docs));
}

export function listDocuments(): DocumentSummary[] {
  return readDocs()
    .map((d) => ({
      id: d.id,
      name: d.name,
      theme: d.theme,
      updatedAt: d.updatedAt,
    }))
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

export function loadDocument(id: string): ComposerDocument | null {
  return readDocs().find((d) => d.id === id) || null;
}

export function saveDocument(doc: ComposerDocument): ComposerDocument {
  const docs = readDocs();
  const idx = docs.findIndex((d) => d.id === doc.id);
  const updated: ComposerDocument = { ...doc, updatedAt: Date.now() };
  if (idx === -1) docs.push(updated);
  else docs[idx] = updated;
  writeDocs(docs);
  return updated;
}

export function deleteDocument(id: string) {
  writeDocs(readDocs().filter((d) => d.id !== id));
  if (getActiveId() === id) setActiveId(null);
}

export function duplicateDocument(id: string): ComposerDocument | null {
  const original = loadDocument(id);
  if (!original) return null;
  const cloned: ComposerDocument = {
    ...original,
    id: newId('doc'),
    name: `${original.name} (copy)`,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  return saveDocument(cloned);
}

export function getActiveId(): string | null {
  return localStorage.getItem(ACTIVE_KEY);
}

export function setActiveId(id: string | null) {
  if (id) localStorage.setItem(ACTIVE_KEY, id);
  else localStorage.removeItem(ACTIVE_KEY);
}

export function makeNewDocument(seed: Partial<ComposerDocument> = {}): ComposerDocument {
  return {
    id: newId('doc'),
    name: 'Untitled document',
    theme: '',
    meta: {},
    tree: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
    ...seed,
  };
}
